import {useEffect} from 'react'
import {useSelector} from 'react-redux'
import {useVioTag} from 'hooks/useVioTag'
import {getRoomOfferAnalyticsContexts} from 'middleware/analytics/selectors'
import {getMeta} from 'modules/meta/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'
import {
  getFilteredRoomsData,
  getIsRoomsPollingComplete,
  getIsRoomsPollingWaitingForResults,
  getRoomCardEntities,
  getRoomOfferAccessConfig
} from 'modules/searchApi/selectors'
import {getIsFromGha, getIsFromGVR} from 'utils/trafficSource'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {getShouldSeeOffersUnlocked} from '@vio/core/src/auth/modules/selectors'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {Offer, OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {
  RoomCardEntity,
  RoomCardEntityType,
  SplitBooking
} from '@vio/core/src/room/types/room'

import {useAccommodationTopOffers} from '../useAccommodationTopOffers'

interface Props {
  hotelId: string
  component: string
}

const getClickedOfferFromEntity = (entity: RoomCardEntity) => {
  return entity?.type === RoomCardEntityType.SplitBooking
    ? entity?.data
    : entity?.data?.offers?.find(offer => offer.isClicked)
}

const getCheapestOfferFromEntity = (entity: RoomCardEntity) => {
  return entity?.type === RoomCardEntityType.SplitBooking
    ? entity?.data
    : entity?.data?.offers?.[0]
}

/**
 * Shared derived data for rendering the primary (preselected) room offer,
 * used by both the desktop PrimaryRoomOffer layout and the compact AP
 * bottom-bar layout.
 */
export const usePrimaryRoomOfferData = ({hotelId, component}: Props) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {inView, ref} = useInView({
    triggerOnce: true,
    threshold: 0.5
  })
  const shouldSeeOffersUnlocked = useSelector(getShouldSeeOffersUnlocked)
  const {splitBooking} = useSelector(getFilteredRoomsData)
  const isRoomsPollingWaitingForResults = useSelector(
    getIsRoomsPollingWaitingForResults
  )
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)

  // getRoomCardEntities ranks split booking first, so it wins over the first
  // room offer whenever one is available.
  const entities = useSelector(getRoomCardEntities)
  const pickedEntity = entities[0]

  const isClicked = Boolean(
    pickedEntity?.type === RoomCardEntityType.Regular
      ? pickedEntity?.data.hasClickedOffer
      : pickedEntity?.data.isClicked
  )

  const pickedOffer = isClicked
    ? getClickedOfferFromEntity(pickedEntity)
    : getCheapestOfferFromEntity(pickedEntity)
  const isYourChoice = hasOfferTag(pickedOffer, OfferTagLabel.IsYourChoice)
  const isSelected = isClicked || isYourChoice
  const isSplitBookingPicked =
    pickedEntity?.type === RoomCardEntityType.SplitBooking

  const analyticsContext = useSelector(state =>
    getRoomOfferAnalyticsContexts(
      state,
      isSplitBookingPicked
        ? (pickedOffer as SplitBooking)?.bundleId
        : (pickedOffer as Offer)?.id,
      isSplitBookingPicked
    )
  )
  const payload = {
    isOfferLocked: false,
    shouldSeeOffersUnlocked
  }

  const {currencyCode} = useSelector(getMeta)
  const displayPrice = getDisplayPrice(pickedOffer?.prices || [])
  const offerTotalPrice = getTotalPrice(displayPrice)
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const anchorPrice = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const showTotalPrices = useSelector(getShowTotalPrices)
  const {nightlyDisplayTotal} = displayPrice?.chargeable || {}

  const {brandName} = useBrand()
  const vioTagDetails = useVioTag({
    price: showTotalPrices ? offerTotalPrice : nightlyDisplayTotal,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    offerTags: pickedOffer?.tags
  })

  const regularOfferId = (pickedOffer as Offer)?.id
  const flowType = useSelector(state =>
    getRoomOfferAccessConfig(state, regularOfferId)
  )

  const isFromGha = useSelector(getIsFromGha)
  const isFromGVR = useSelector(getIsFromGVR)
  const shouldShowFreeCancellationDate = isClicked && (isFromGha || isFromGVR)

  const trackOfferClicked = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.Offer,
        action: Action.Clicked,
        page: Page.Accommodation,
        component,
        analyticsContext,
        payload
      })
    )
  }

  useEffect(() => {
    if (inView)
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.Offer,
          action: Action.Viewed,
          page: Page.Accommodation,
          component,
          analyticsContext,
          payload
        })
      )
  }, [inView])

  // for split booking we'll have two offers, for a regular offer - only one
  const labelOffers: Offer[] = isSplitBookingPicked
    ? [
        {
          ...pickedEntity.data.offers[0].offer,
          // reading the cancellationPenalties from the bundle level
          cancellationPenalties: splitBooking.cancellationPenalties
        },
        {
          ...pickedEntity.data.offers[1].offer,
          // reading the cancellationPenalties from the bundle level
          cancellationPenalties: splitBooking.cancellationPenalties
        }
      ]
    : [pickedOffer as Offer]

  return {
    ref,
    isRoomsPollingWaitingForResults,
    isRoomsPollingComplete,
    pickedEntity,
    pickedOffer,
    isSelected,
    isSplitBookingPicked,
    splitBooking,
    labelOffers,
    vioTagDetails,
    brandName,
    regularOfferId,
    flowType,
    shouldShowFreeCancellationDate,
    trackOfferClicked
  }
}
